import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import remarkGfm from "remark-gfm";
import InfiniteScroll from "react-infinite-scroll-component";
import CircularProgress from "@material-ui/core/CircularProgress";
import download from "downloadjs";
import { WhiteBlueTooltip } from "./UIComponents";
import "./AITestDetailsInsight.css";
import AIExecutionFailInsightsChart from "./AIExecutionFailInsightsChart";

const AIExecutionFailInsights = ({ executionId, project, failCount }) => {
  const [showModal, setShowModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [, setDataLoading] = useState(true);
  const [allAIData, setAllAIData] = useState([]);
  const [, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [exportStatus, setExportStatus] = useState(false);
  const [exportErrorMsg, setExportErrorMsg] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [graphStatus, setGraphStatus] = useState(false);
  const [graphErrorMsg, setGraphErrorMsg] = useState(null);

  const chatWrapperRef = useRef();

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: failCount == 0 ? "50vw" : "90vw",
    maxHeight: "90vh",
    bgcolor: "#f1f1f1",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
    borderRadius: "10px",
    overflowY: "auto",
    p: 4,
    fontFamily: `"Inter", "Segoe UI", Roboto, sans-serif`,
    color: "#2e2e2e",
  };

  useEffect(() => {
    if (showModal) {
      setAllAIData([]);
      setErrorMsg(null);
      setPage(1);
      if (failCount > 0) {
        setHasMore(true);
        setDataLoading(true);
        fetchAIInsight(1);
        getGraphInsights();
      } else {
        setHasMore(false);
        setDataLoading(false);
      }
    }
  }, [showModal]);

  const fetchAIInsight = async (pageToFetch) => {
    let isMounted = true;
    const limit = 10;
    const offset = pageToFetch ?? page;

    const apiUrl =
      window.configs.BASE_API_URL + window.configs.TEST_RESULT_INSIGHT_URL;

    const payload = {
      execution_id: executionId,
      project,
      limit,
      offset,
    };

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let newRows = [];

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          if (isMounted) {
            setHasMore(newRows.length > 0 && newRows[0] !== "No test cases");
            setAllAIData((prev) =>
              newRows.length > 0 && newRows[0] !== "No test cases"
                ? [...prev, ...newRows]
                : prev
            );
            setDataLoading(false);
            setPage((prev) => prev + 1);
          }
          break;
        }

        const chunk = decoder.decode(value, { stream: true }).trim();
        if (chunk) {
          const rows = chunk.split("\n").filter((r) => r.trim() !== "");
          newRows.push(...rows);
        }
      }
    } catch (err) {
      if (isMounted) setErrorMsg(err.message);
    } finally {
      if (isMounted) setDataLoading(false);
    }
  };

  const exportFailInsights = async (e) => {
    e.stopPropagation();
    let isMounted = true;
    setExportStatus(true);
    setExportErrorMsg(null);

    const apiUrl =
      window.configs.BASE_API_URL + window.configs.INSIGHTS_FILE_DOWNLOAD_URL;

    const params = {
      execution_id: executionId,
      project,
    };
    const query = new URLSearchParams(params).toString();
    const apiUrlWithParams = `${apiUrl}?${query}`;

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const fetchWithRetry = async (interval = 10000) => {
      try {
        const response = await fetch(apiUrlWithParams, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        if (data.status == "success" && data.export_url) {
          download(data.export_url);
          if (isMounted) {
            setExportStatus(false);
            setExportErrorMsg(null);
          }
          return;
        }
        //file not ready yet
        if (data.status && !data.export_url) {
          setExportStatus(true);
          setExportErrorMsg(data.message);
          await delay(interval);
          return fetchWithRetry(interval);
        }
      } catch (err) {
        if (isMounted) {
          setExportErrorMsg(err.message);
          setExportStatus(false);
        }
      }
    };
    fetchWithRetry();
  };
  const getGraphInsights = async () => {
    let isMounted = true;
    setGraphStatus(true);
    setGraphErrorMsg(null);

    const apiUrl =
      window.configs.BASE_API_URL + window.configs.INSIGHTS_GRAPH_URL;

    const params = {
      execution_id: executionId,
      project,
    };
    const query = new URLSearchParams(params).toString();
    const apiUrlWithParams = `${apiUrl}?${query}`;

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const fetchWithRetry = async (interval = 10000) => {
      try {
        const response = await fetch(apiUrlWithParams, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        if (data.status == "success") {
          setGraphData(data.data);
          if (isMounted) {
            setGraphStatus(false);
            setGraphErrorMsg(null);
          }
          return;
        }
        //graph not ready yet
        if (data.status) {
          setGraphStatus(true);
          setGraphErrorMsg(data.message);
          await delay(interval);
          return fetchWithRetry(interval);
        }
      } catch (err) {
        if (isMounted) {
          setGraphErrorMsg(err.message);
          setGraphStatus(false);
        }
      }
    };
    fetchWithRetry();
  };

  return (
    <>
      <a onClick={() => setShowModal(true)} style={{ marginLeft: 5 }}>
        <AutoAwesomeIcon
          sx={{
            color: "#e8a703ff",
            transition: "all 0.3s ease",
            filter: "drop-shadow(0 0 2px rgba(255, 223, 0, 0.8))",
            "&:hover": {
              color: "#ffbf00",
              transform: "scale(1.1) rotate(4deg)",
              filter: "drop-shadow(0 0 5px rgba(255, 223, 0, 0.8))",
            },
          }}
        />
      </a>

      <Modal
        open={showModal}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setShowModal(false);
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={modalStyle}
          position="relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Export button */}
          <WhiteBlueTooltip title="Export" placement="left" arrow>
            <IconButton
              sx={{
                position: "absolute",
                top: 15,
                right: 75,
                outline: "none",
                color: "#3a7b0eff",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#f0f7f2f9",
                  outline: "none",
                  filter: "drop-shadow(0 1px 2px rgba(99, 214, 117, 0.598))",
                },
              }}
              disableRipple
              aria-label="export"
              disabled={exportStatus || failCount === 0}
              onClick={(e) => {
                //api call for export
                exportFailInsights(e);
              }}
            >
              <DownloadOutlinedIcon />
              {exportStatus && (
                <CircularProgress size={24} className="buttonProgressLoader" />
              )}
            </IconButton>
          </WhiteBlueTooltip>
          {exportErrorMsg && (
            <div className="export-status-message">{exportErrorMsg}</div>
          )}

          {/* Close button */}
          <IconButton
            onClick={() => {
              setShowModal(false);
              setAllAIData([]);
              setErrorMsg(null);
              setDataLoading(true);
              setExportErrorMsg(null);
              setExportStatus(false);
            }}
            sx={{
              position: "absolute",
              top: 15,
              right: 12,
              color: "#33426edf",
              outline: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                outline: "none",
                backgroundColor: "#f0f7f2f9",
                filter: "drop-shadow(0 1px 2px rgba(88, 137, 244, 0.6))",
              },
            }}
            disableRipple
            disableFocusRipple
            aria-label="close"
          >
            <CloseOutlinedIcon />
          </IconButton>

          <h4 className="modal-heading">AI Test Failure Insights</h4>

          <div
            ref={chatWrapperRef}
            id="mainScrollableDiv"
            className="markdown-container"
            style={{ height: "60vh", overflow: "auto" }}
          >
            {errorMsg && <div className="error">{errorMsg}</div>}
            {failCount > 0 && (
              <div style={{ marginTop: "1rem" }}>
                {/* Graph status / error message section */}
                {graphStatus && graphErrorMsg && (
                  <span className="graph-status-message">
                    {graphErrorMsg} for visuals
                  </span>
                )}
                {failCount > 0 && !graphStatus && graphData && (
                  <div
                    className="visual-container double-column"
                  >
                    <AIExecutionFailInsightsChart
                      dataArray={graphData.failure_category_vs_module}
                      type="module"
                    />
                    <AIExecutionFailInsightsChart
                      dataArray={graphData.failure_category_vs_priority}
                      type="priority"
                    />
                  </div>
                )}
              </div>
            )}
            {/* Failure Aggregates Table */}
            {graphData?.failure_aggregates?.length > 0 && (
              <div style={{ marginTop: "2rem" }}>
                <h6
                  style={{
                    marginBottom: "1rem",
                    textAlign: "left",
                    color: "#33426e",
                  }}
                >
                  Failure Category Summary
                </h6>
                <table
                  className="markdown-container"
                  style={{ height: "auto", marginBottom: "2rem" }}
                >
                  <thead>
                    <tr>
                      <th>Failure Category</th>
                      <th>Note</th>
                      <th>Test Count</th>
                      <th>Test Names</th>
                    </tr>
                  </thead>
                  <tbody>
                    {graphData.failure_aggregates.map((item, index) => (
                      <tr key={index}>
                        <td>{item["Failure Category"]}</td>
                        <td>{item.Note || "-"}</td>
                        <td>{item.count}</td>
                        <td>
                          <ul style={{ margin: 0, paddingLeft: "20px" }}>
                            {item.test_names?.map((name, i) => (
                              <li key={i}>{name}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <InfiniteScroll
              dataLength={allAIData.length}
              next={() => fetchAIInsight()}
              hasMore={allAIData.length < failCount}
              scrollableTarget="mainScrollableDiv"
              loader={
                <div className="spinner-container">
                  <div className="spinner" style={{ marginTop: "1rem" }} />
                </div>
              }
              endMessage={
                <p className="end-text">
                  {failCount == 0 ? "No Failed Tests" : "End of Insights"}
                </p>
              }
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {allAIData.join("\n")}
              </ReactMarkdown>
            </InfiniteScroll>
          </div>
        </Box>
      </Modal>
    </>
  );
};

AIExecutionFailInsights.propTypes = {
  executionId: PropTypes.string.isRequired,
  project: PropTypes.string.isRequired,
};

export default AIExecutionFailInsights;
